import { fromEvent, Observable, of, race, timer } from 'rxjs';
import { filter, mapTo, switchMap, take, takeUntil } from 'rxjs/operators';

import { MAT_SNACK_BAR_DATA, MatSnackBarRef } from '@angular/material/snack-bar';
import { ChangeDetectionStrategy, Component, ElementRef, inject, OnInit } from '@angular/core';

import { LifecycleHooks } from '@shared/services/lifecycle-hooks.service';
import { SnackbarOptions } from '@shared/models/notice.models';

@Component({
  templateUrl: './snackbar.component.html',
  styleUrls: ['./snackbar.component.scss'],
  providers: [LifecycleHooks],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class Snackbar implements OnInit {
  data = inject<{
    options: SnackbarOptions;
    title: string;
  }>(MAT_SNACK_BAR_DATA);
  private snackBarRef = inject<MatSnackBarRef<Snackbar>>(MatSnackBarRef);
  private elRef = inject(ElementRef);
  private lh = inject(LifecycleHooks);

  readonly options: SnackbarOptions = {
    color: 'primary',
    ...this.data.options,
  };

  get parentNode(): Element {
    return this.elRef.nativeElement.parentNode;
  }

  ngOnInit(): void {
    if (!this.options.persistent) {
      this.raceWithEnter(this.options.timeout || 5000)
        .pipe(
          take(1),
          switchMap((close) =>
            close ? of(true) : fromEvent(this.parentNode, 'mouseleave').pipe(switchMap(() => this.raceWithEnter(3000))),
          ),
          filter((close) => close),
          take(1),
          takeUntil(this.lh.destroy),
        )
        .subscribe(() => this.dismiss());
    }
  }

  onAction(): void {
    this.options.actionCallback?.();
    this.snackBarRef.dismissWithAction();
  }

  dismiss(): void {
    this.snackBarRef.dismiss();
    this.options.actionCallback = null;
  }

  private raceWithEnter(timeout: number): Observable<boolean> {
    return race(timer(timeout).pipe(mapTo(true)), fromEvent(this.parentNode, 'mouseenter').pipe(mapTo(false)));
  }
}
